import { supabase } from '../supabase.config'

const normalizeValue = (value) => String(value || '').trim()

const isDeliveredStatus = (status) => normalizeValue(status).toLowerCase() === 'delivered'

const getAssignmentLrNumbers = (assignment) =>
  Array.isArray(assignment?.lr_numbers) ? assignment.lr_numbers.map(normalizeValue).filter(Boolean) : []

const loadOrdersForReferences = async (references) => {
  const refs = Array.from(new Set((references || []).map(normalizeValue).filter(Boolean)))
  if (refs.length === 0) return []

  const queries = await Promise.all([
    supabase.from('orders').select('id, status, order_number, tracking_number, order_id').in('order_number', refs),
    supabase.from('orders').select('id, status, order_number, tracking_number, order_id').in('tracking_number', refs),
    supabase.from('orders').select('id, status, order_number, tracking_number, order_id').in('order_id', refs),
  ])

  const merged = new Map()
  for (const result of queries) {
    if (result.error) {
      console.error('Failed to load linked orders for delivery assignment', result.error)
      continue
    }
    for (const row of result.data || []) {
      if (row?.id) merged.set(row.id, row)
    }
  }

  return Array.from(merged.values())
}

const calculateAssignmentStatus = async (assignment) => {
  const lrNumbers = getAssignmentLrNumbers(assignment)
  if (lrNumbers.length === 0) return null

  const linkedOrders = await loadOrdersForReferences(lrNumbers)
  const deliveredRefs = new Set()

  linkedOrders.forEach((order) => {
    if (!isDeliveredStatus(order.status)) return
    ;[order.order_number, order.tracking_number, order.order_id].forEach((ref) => {
      const normalized = normalizeValue(ref)
      if (normalized) deliveredRefs.add(normalized)
    })
  })

  const allDelivered = lrNumbers.every((lr) => deliveredRefs.has(lr))
  return allDelivered ? 'Delivered' : 'Delivery Pending'
}

export const syncDeliveryAssignmentStatusForOrder = async (orderRow) => {
  if (!orderRow) return

  const references = Array.from(new Set([
    normalizeValue(orderRow.order_number),
    normalizeValue(orderRow.tracking_number),
    normalizeValue(orderRow.order_id),
    normalizeValue(orderRow.id),
  ].filter(Boolean)))

  if (references.length === 0) return

  const { data: assignments, error: assignmentsError } = await supabase
    .from('delivery_boy_data')
    .select('id, lr_numbers, status, delivery_boy_status')
    .order('created_at', { ascending: false })

  if (assignmentsError) {
    console.error('Failed to load delivery assignments', assignmentsError)
    return
  }

  const matchingAssignments = (assignments || []).filter((assignment) => {
    const lrNumbers = getAssignmentLrNumbers(assignment)
    return lrNumbers.some((lr) => references.includes(lr))
  })

  for (const assignment of matchingAssignments) {
    const nextDeliveryStatus = await calculateAssignmentStatus(assignment)
    if (!nextDeliveryStatus) continue
    if (
      normalizeValue(assignment.delivery_boy_status) === nextDeliveryStatus &&
      normalizeValue(assignment.status) === nextDeliveryStatus
    ) {
      continue
    }

    const { error: updateError } = await supabase
      .from('delivery_boy_data')
      .update({
        delivery_boy_status: nextDeliveryStatus,
        status: nextDeliveryStatus,
      })
      .eq('id', assignment.id)

    if (updateError) {
      console.error('Failed to update delivery assignment status', updateError)
    }
  }
}

export const syncAllDeliveryAssignmentStatuses = async () => {
  const { data: assignments, error } = await supabase
    .from('delivery_boy_data')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Failed to load delivery assignments', error)
    return []
  }

  const syncedAssignments = []
  for (const assignment of assignments || []) {
    const nextDeliveryStatus = await calculateAssignmentStatus(assignment)
    if (!nextDeliveryStatus) {
      syncedAssignments.push(assignment)
      continue
    }

    if (
      normalizeValue(assignment.delivery_boy_status) !== nextDeliveryStatus ||
      normalizeValue(assignment.status) !== nextDeliveryStatus
    ) {
      const { error: updateError } = await supabase
        .from('delivery_boy_data')
        .update({
          delivery_boy_status: nextDeliveryStatus,
          status: nextDeliveryStatus,
        })
        .eq('id', assignment.id)

      if (updateError) {
        console.error('Failed to update delivery assignment status', updateError)
        syncedAssignments.push(assignment)
        continue
      }
    }

    syncedAssignments.push({
      ...assignment,
      delivery_boy_status: nextDeliveryStatus,
      status: nextDeliveryStatus,
    })
  }

  return syncedAssignments
}
